import CircleIcon from '@mui/icons-material/Circle'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useCustom } from '@refinedev/core'
import React, { useState } from 'react'

type Status = 'green' | 'amber' | 'red'

interface HealthCheck {
  name: string
  status: Status
  message: string
}

interface HealthReport {
  status: Status
  generated_at: string
  environment: string
  runbook_url?: string
  checks: HealthCheck[]
}

const COLOUR: Record<Status, string> = {
  green: '#2e7d32',
  amber: '#ed6c02',
  red: '#d32f2f',
}

const LABEL: Record<Status, string> = {
  green: 'All checks healthy',
  amber: 'Degraded',
  red: 'Unhealthy',
}

// The traffic light is a monitor: it must reflect the state as of now, not as of
// page load, so it re-reads on an interval. Every check behind the endpoint is a
// live round-trip to Redis, the DB, Qdrant, the API and a socket, so the closed
// light ticks slowly; while an operator has the panel open and is watching a
// service come back, it ticks fast.
const REFRESH_MS = 60_000
const OPEN_REFRESH_MS = 5_000

const prettyName = (name: string) =>
  name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())

export const ServiceHealthLight: React.FC = () => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const open = Boolean(anchor)

  const { data, isLoading, isError, refetch } = useCustom<HealthReport>({
    url: '/health/services',
    method: 'get',
    errorNotification: false,
    queryOptions: {
      refetchInterval: open ? OPEN_REFRESH_MS : REFRESH_MS,
      refetchOnWindowFocus: true,
    },
  })

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget)
    // Opening the panel is a request for the state now, not for whatever the
    // last background tick happened to catch.
    refetch()
  }

  const report = data?.data
  // A failed fetch is itself a red: we cannot show green for a service whose
  // health we could not read.
  const status: Status = report?.status ?? (isError ? 'red' : 'green')
  const checks = report?.checks ?? []
  const problems = checks.filter((c) => c.status !== 'green')

  const summary = isLoading
    ? 'Checking service health...'
    : isError
      ? 'Could not read service health'
      : `${LABEL[status]}${problems.length ? ` - ${problems.length} check(s) need attention` : ''}`

  return (
    <>
      <Tooltip title={summary}>
        <span>
          <IconButton
            onClick={handleOpen}
            aria-label={`Service health: ${summary}`}
            data-status={status}
            size="small"
            sx={{ mr: 1 }}
          >
            {isLoading && !report ? (
              <CircularProgress size={16} sx={{ color: 'inherit' }} />
            ) : (
              <CircleIcon sx={{ color: COLOUR[status], fontSize: 18 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 340, maxWidth: 480 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Service health
            </Typography>
            <Chip
              size="small"
              label={status.toUpperCase()}
              sx={{ bgcolor: COLOUR[status], color: '#fff', fontWeight: 600 }}
            />
          </Stack>

          {isError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              Could not reach the health endpoint.
            </Typography>
          )}

          <Stack divider={<Divider flexItem />} spacing={0.5}>
            {checks.map((check) => (
              <Stack
                key={check.name}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ py: 0.5 }}
              >
                <CircleIcon
                  sx={{ color: COLOUR[check.status], fontSize: 12, mt: '5px' }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {prettyName(check.name)}
                  </Typography>
                  {check.status !== 'green' && (
                    <Typography variant="caption" color="text.secondary">
                      {check.message}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>

          {report && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5 }}
            >
              {report.environment} - checked{' '}
              {new Date(report.generated_at).toLocaleTimeString()}
              {report.runbook_url && (
                <>
                  {' - '}
                  <Link
                    href={report.runbook_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    runbook
                  </Link>
                </>
              )}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  )
}
